import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import { ReusableInboundEvents } from '~/components/Builder/Mobile/builderMobileApi.enums';
import { IMobileAppService } from '~/components/Builder/Mobile/builderMobileApi.types';
import BaseAbstractBuilderApi from '~/components/Builder/Mobile/Entities/BaseAbstractBuilderApi';
import { IFlowTemplate, TInvoiceTypes } from '~/types/model.types';
import { TBlock } from '~/types/builder.types';
import { BlockController, IFindBlockBy } from '~/controllers/block.controller';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { FlowMXContextValue } from '~/components/Builder/FlowMX.provider';

export class BuilderMobileReusableSFApi extends BaseAbstractBuilderApi {
    constructor(
        rootStore: RootStore,
        flowMXContext: FlowMXContextValue,
        MobileAppService: IMobileAppService
    ) {
        super(rootStore, flowMXContext, MobileAppService);
        Object.values(ReusableInboundEvents).forEach(reusableInboundEvent => {
            this.subscribeToEvent(reusableInboundEvent);
        });
    }

    // INBOUND
    async [ReusableInboundEvents.ApplyReusableSFTemplate]({
        template,
        blockType,
        blockId
    }: {
        template: IFlowTemplate;
        blockType: string;
        blockId?: string;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedBlock, resetOverlays },
            toastStore
        } = this.rootStore;

        if (!selectedBlock) {
            return;
        }
        if (!template) {
            // Blank template
            BuilderMobileReusableSFApi.startFromBlankTemplate({
                blockType,
                selectedBlock,
                blockCtrl
            });
        } else {
            try {
                const options: IFindBlockBy = {};

                if (blockType === 'payment_schedule_block') {
                    options.blockType = blockType;
                } else {
                    options.blockId = blockId;
                }
                await blockCtrl.importSingularActionBlockFromFlow(
                    selectedBlock,
                    template._id,
                    options
                );
                toastStore.add({
                    type: ToastType.Success,
                    text: `${
                        blockType === 'invoice_container_block'
                            ? 'Invoice'
                            : 'Contract'
                    } from ${
                        template.title
                    } was added successfully and the current theme was applied. Any changes made will only apply here.`
                });
            } catch (e) {
                toastStore.add({
                    type: ToastType.Error,
                    text: `Something went wrong...the selected template could not be added`,
                    actions: [
                        {
                            text: 'START FROM BLANK',
                            size: 'sm',
                            theme: 'secondary-invisible-primary',
                            onClick: () => {
                                BuilderMobileReusableSFApi.startFromBlankTemplate(
                                    {
                                        blockType,
                                        selectedBlock,
                                        blockCtrl
                                    }
                                );
                            }
                        }
                    ]
                });
            }
        }
        resetOverlays();
    }

    private static startFromBlankTemplate({
        blockType,
        selectedBlock,
        blockCtrl
    }: {
        blockType: string;
        selectedBlock: TBlock;
        blockCtrl: BlockController;
    }) {
        if (blockType === 'invoice_container_block') {
            const invoiceBlock = selectedBlock as InvoiceBlock;
            blockCtrl.createBlankInvoice(
                TInvoiceTypes.PaymentSchedule,
                invoiceBlock,
                'invoice_container_block'
            );
        }
        if (blockType === 'contract_block') {
            blockCtrl.replaceBlockWithBlock(selectedBlock, blockType);
        }
    }
}
